import { Flex, FormLabel, H2, P } from '@dnb/eufemia';
import { Field, Form } from '@dnb/eufemia/extensions/forms';
import { exhaustiveArray } from '@portals/shared/common/utils/typeUtils';
import { type JSX } from 'react';

import MarkdownEditor from '@/components/MarkdownEditor';
import type { McpRequest } from '@/openapi/schema';
import { capitalizeFirstLetterOfString } from '@/utils/textUtils';

import type { AddMcpFormData } from './schema';

// Runtime lists of the OpenAPI enum values; exhaustiveArray fails typecheck if a
// value is missing. Labels are derived (LAUNCHED -> Launched), not maintained.

const CLASSIFICATIONS = exhaustiveArray<McpRequest['classification']>(['SYSTEM', 'DOMAIN', 'EXPERIENCE', 'TECHNICAL']);

const STAGES = exhaustiveArray<McpRequest['stage']>(['LAUNCHED', 'UPCOMING', 'DEPRECATED', 'DECOMMISSIONED']);

const toLabel = (value: string): string => capitalizeFirstLetterOfString(value.toLowerCase());

type McpDetailsStepProps = {
  formId: string;
};

export default function McpDetailsStep({ formId }: McpDetailsStepProps): JSX.Element {
  const { data, update } = Form.useData<AddMcpFormData>(formId);

  return (
    <Flex.Stack>
      <div>
        <H2 bottom="small" top="0">
          MCP details
        </H2>
        <P>Describe the MCP you are publishing.</P>
      </div>

      <Field.String label="Name" maxLength={255} path="/name" required size="large" width="stretch" />

      <Field.String
        label="Slug"
        onChangeValidator={(value) =>
          /^[\da-z]+(?:-[\da-z]+)*$/.test(value ?? '')
            ? undefined
            : new Error('Use lowercase letters and numbers, separated by single hyphens')
        }
        path="/slug"
        required
        size="large"
        width="stretch"
      />

      <Field.String
        label="Description"
        maxLength={400}
        multiline
        path="/description"
        rows={4}
        size="large"
        width="stretch"
      />

      <div className="grid grid-cols-2 gap-4">
        <Field.Selection label="Classification" path="/classification" size="large" variant="dropdown" width="stretch">
          {CLASSIFICATIONS.map((value) => (
            <Field.Option key={value} title={toLabel(value)} value={value} />
          ))}
        </Field.Selection>

        <Field.Selection label="Stage" path="/stage" required size="large" variant="dropdown" width="stretch">
          {STAGES.map((value) => (
            <Field.Option key={value} title={toLabel(value)} value={value} />
          ))}
        </Field.Selection>
      </div>

      <div>
        <FormLabel bottom="small">Guide</FormLabel>
        <MarkdownEditor height={600} onChange={(value) => update('/guide', value ?? '')} value={data?.guide} />
      </div>
    </Flex.Stack>
  );
}
